// One row of a Settings list kept by its commands (V-10): a Dimension's value or an expense account, its name and
// what it is, and the Rename and Retire buttons the person's Roles allow. Retiring asks once more first: nothing undoes
// it, though it stays on what was posted.
import { useState, type FormEvent, type ReactNode } from "react";
import { ApiError } from "../api.js";
import { useWords, type Words } from "../i18n/words.js";

/** Why a change did not happen, in the person's words: the command's own words, or that their Roles don't allow it. */
export function changeProblem(error: unknown, w: Words): string {
  if (!(error instanceof ApiError)) return w.settingsUnreachable;
  if (error.status === 403) return w.listNotAllowed;
  return error.problems.length > 0 ? error.problems.join(" ") : w.settingsRefused(error.code);
}

export function ListRow({
  name,
  details,
  retired,
  mayRename,
  mayRetire,
  busy,
  onRename,
  onRetire,
}: {
  name: string;
  /** What it is beside its name: a code, the Counterparty it stands for, where it comes from. */
  details?: ReactNode;
  retired: boolean;
  mayRename: boolean;
  mayRetire: boolean;
  busy: boolean;
  /** Renames it; true when it was. */
  onRename: (name: string) => Promise<boolean>;
  onRetire: () => Promise<boolean>;
}) {
  const w = useWords();
  const [renaming, setRenaming] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [text, setText] = useState(name);

  const rename = async (event: FormEvent) => {
    event.preventDefault();
    if (await onRename(text.trim())) setRenaming(false);
  };

  return (
    <li className="item">
      <div>
        <h3 translate="no">{name}</h3>
        {details ? <p className="small muted">{details}</p> : null}
      </div>
      <div>{retired ? <span className="tag tag-plain">{w.listRetired}</span> : null}</div>
      {retired || (!mayRename && !mayRetire) ? null : renaming ? (
        <form className="acts" onSubmit={(event) => void rename(event)}>
          <label className="field">
            {w.listNewName(name)}{" "}
            <input value={text} maxLength={120} autoFocus onChange={(event) => setText(event.target.value)} />
          </label>
          <button type="submit" className="btn btn-go" disabled={busy || text.trim() === "" || text.trim() === name}>
            {w.listSave}
          </button>
          <button type="button" className="btn" onClick={() => setRenaming(false)}>
            {w.listCancel}
          </button>
        </form>
      ) : retiring ? (
        <div className="acts" role="group" aria-label={w.listRetire}>
          <p>{w.listRetireSure(name)}</p>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={() => void onRetire().then((done) => done || setRetiring(false))}
          >
            {w.listRetireConfirm}
          </button>
          <button type="button" className="btn" onClick={() => setRetiring(false)}>
            {w.listCancel}
          </button>
        </div>
      ) : (
        <div className="acts">
          {mayRename ? (
            <button
              type="button"
              className="btn"
              aria-label={`${w.listRename}: ${name}`}
              onClick={() => {
                setText(name);
                setRenaming(true);
              }}
            >
              {w.listRename}
            </button>
          ) : null}
          {mayRetire ? (
            <button
              type="button"
              className="btn"
              aria-label={`${w.listRetire}: ${name}`}
              onClick={() => setRetiring(true)}
            >
              {w.listRetire}
            </button>
          ) : null}
        </div>
      )}
    </li>
  );
}
